import { useTranslation } from 'react-i18next';
import { LiliNotification } from '@lili/lili-frontend-common';
import { useBlocker } from '@tanstack/react-router';
import { useUnifiedKybPolling } from '@Hooks/polling/useUnifiedKybPolling';
import { isLiliAppVersionAtLeast } from '@Services/utils/utils.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { DeviceType } from '@Types/onboarding.types.ts';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { NonUsSocureLogicHook } from './NonUsSocureLogic.hook';
import { DocVScreen } from './components/DocVScreen';
import { actOnNonUseSocure } from './NonUsSocureAnalytics';

export interface SocureLogicHookResult {
  onSendSmsClick: () => void;
  openResendOption: boolean;
  timeLeft: string | null;
  isLoading: boolean;
  handleFinishedVerification?: () => void;
  errorMessage?: string;
  socureQR?: string;
  isSuccessNotificationVisible: boolean;
  setIsSuccessNotificationVisible: (visible: boolean) => void;
}

export interface NonUSSocurePageProps {
  socureData: { qrCode?: string; url?: string };
}

export interface NonUSSocurePageWithLogicProps {
  socureData: { qrCode?: string; url?: string };
  logic: SocureLogicHookResult;
  skipDefaultPolling?: boolean;
  customDescription?: string;
}

export const NonUSSocurePageLayout = ({
  socureData,
  logic,
  skipDefaultPolling = false,
  customDescription,
}: NonUSSocurePageWithLogicProps) => {
  const { t } = useTranslation('nonUsSocure');

  const {
    onSendSmsClick,
    openResendOption,
    timeLeft,
    isLoading,
    handleFinishedVerification,
    errorMessage,
    socureQR,
    isSuccessNotificationVisible,
    setIsSuccessNotificationVisible,
  } = logic;

  useUnifiedKybPolling(handleFinishedVerification, 5400000, 2700, !skipDefaultPolling);

  const { isFromLiliApp, deviceType } = useBoundStore((state) => state.onboardingSlice);

  const openVerificationLink = () => {
    actOnNonUseSocure('open external socure link');
    if (!socureData?.url) return;

    if (isFromLiliApp && isLiliAppVersionAtLeast('26.2.1')) {
      if (deviceType === DeviceType.IOS) {
        window.location.replace('liliapp://socure?url=' + encodeURIComponent(socureData.url));
        return;
      }
      if (deviceType === DeviceType.ANDROID) {
        window.androidApp.externalSocure(socureData.url);
        return;
      }
    }

    window.open(socureData.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <DocVScreen
      onSendSmsClick={onSendSmsClick}
      openResendOption={openResendOption}
      timeLeft={timeLeft}
      isLoading={isLoading}
      qrCode={socureQR || socureData?.qrCode}
      errorMessage={errorMessage}
      onContinue={openVerificationLink}
      customDescription={customDescription}
      notification={
        isSuccessNotificationVisible && (
          <LiliNotification
            id='success-notification'
            text={t('mainContent.newLinkCreatedSuccess')}
            type={2}
            onClose={() => setIsSuccessNotificationVisible(false)}
          />
        )
      }
    />
  );
};

export const NonUSSocurePage = ({ socureData }: NonUSSocurePageProps) => {
  const logic = NonUsSocureLogicHook();

  useBlocker({
    shouldBlockFn: ({ next }) => {
      const allowedRoutes: string[] = [
        routesModel[RoutesMap.SUCCESS].path,
        routesModel[RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN].path,
        routesModel[RoutesMap.KYC_FAILED].path,
        routesModel[RoutesMap.VERIFICATION_CENTER].path,
        routesModel[RoutesMap.UNDER_REVIEW].path,
      ];
      return !allowedRoutes.includes(next.routeId);
    },
  });

  return <NonUSSocurePageLayout socureData={socureData} logic={logic} />;
};
